import { AppContext } from "../context/AppContext";
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "../helpers/directusClient";
import { Link } from "react-router-dom";
import { CalendarService } from "../utils/apiService";
import {
  DollarOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  BulbFilled,
  BankOutlined,
  CalendarOutlined,
  SettingOutlined,
  WarningOutlined,
  PlayCircleOutlined,
  LayoutOutlined,
} from "@ant-design/icons";
import {
  Space,
  Typography,
  Button,
  Badge,
  Switch,
  Tooltip,
  Row,
  Col,
  Grid,
  Modal,
  InputNumber,
  Tag,
} from "antd";
import { BellOutlined } from "@ant-design/icons";
import axios from "axios";

// Branch Manager role ID for specific access control
const BRANCH_MANAGER_ROLE_ID = "e689cbb2-3c98-42f1-ab24-291d30e8b270";

const Navbar = () => {
  const {
    user,
    company,
    accounts,
    logout,
    theme,
    toggleTheme,
    skin,
    setCompany,
    navLayout,
    toggleNavLayout,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const { Text } = Typography;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [daysLeft, setDaysLeft] = useState(7);
  const [trialStatus, setTrialStatus] = useState("active");
  const [planStatus, setPlanStatus] = useState("active");
  const [todayRemindersCount, setTodayRemindersCount] = useState(0);
  const [todayDisbursedTotal, setTodayDisbursedTotal] = useState(0);
  const [dailyDisbursementLimit, setDailyDisbursementLimit] = useState(0);
  const [dailyDisbursementWarnPct, setDailyDisbursementWarnPct] = useState(80);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitDraft, setLimitDraft] = useState(0);
  const [warnPctDraft, setWarnPctDraft] = useState(80);

  const [notificationCount, setNotificationCount] = useState();
  const [pendingExpensesCount, setPendingExpensesCount] = useState(0);
  const [loansDueToday, setLoansDueToday] = useState(0);

  const formatAmount = (value, compact = false) => {
    const amount = Number(value || 0);
    if (compact) {
      if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
      return amount.toFixed(0);
    }
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  useEffect(() => {
    if (company?.plan_enddate) {
      const calculateTrialDays = () => {
        const today = new Date();
        const endDate = new Date(company.plan_enddate);
        const timeDiff = endDate - today;
        const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        if (company.plan > 0) {
          // Paid Plan Logic
          if (daysRemaining > 7) {
            setPlanStatus("inactive"); // Don't show renew banner
          } else if (daysRemaining > 0) {
            setDaysLeft(daysRemaining);
            setPlanStatus("active");
          } else {
            setPlanStatus("expired");
            navigate("/billing");
          }
        } else {
          // Free Trial Logic
          if (daysRemaining > 0 && daysRemaining <= 7) {
            setDaysLeft(daysRemaining);
            setTrialStatus("active");
          } else if (daysRemaining > 7) {
            setTrialStatus("inactive"); // Shouldn't normally happen for trials
          } else {
            setTrialStatus("expired");
            navigate("/billing");
          }
        }
      };

      calculateTrialDays();
    }
  }, [company]);

  const handleLogout = async () => {
    try {
      // First try to logout from Directus API
      await client.auth.logout();
    } catch (error) {
      console.error("Error logging out from API:", error);
      // Continue with local logout even if API logout fails
    }

    // Clear browser cache if available
    try {
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
        }
      }
    } catch (cacheError) {
      console.error("Error clearing cache:", cacheError);
    }

    // Call the context logout which will handle state clearing and redirection
    logout();
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[loan_status]=2&filter[company]=${company?.id}`,
        );

        setNotificationCount(response.data.data.length);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
  }, []);

  // Fetch pending expenses count for administrators
  useEffect(() => {
    const fetchPendingExpenses = async () => {
      // Only show for administrators
      if (user?.role !== "Administrator" || !company?.id) return;

      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://zedlenders.pickmesms.com/items/transactions",
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: {
                company: company.id,
                transaction_status: "Pending",
                transaction_type: "Operational Cost",
              },
            },
          },
        );

        setPendingExpensesCount(response.data.data?.length || 0);
      } catch (error) {
        console.error("Error fetching pending expenses:", error);
        setPendingExpensesCount(0);
      }
    };

    fetchPendingExpenses();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingExpenses, 30000);
    return () => clearInterval(interval);
  }, [user?.role, company?.id]);

  // Fetch today's reminders count
  useEffect(() => {
    const fetchTodayReminders = async () => {
      if (!user?.user_id || !company?.id) return;

      try {
        const token = localStorage.getItem("token");
        const events = await CalendarService.fetchEvents(
          token,
          user.user_id,
          company.id,
        );

        // Filter events for today
        const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
        const todayEvents = events.filter(
          (event) => event.event_date === today,
        );

        setTodayRemindersCount(todayEvents.length);
      } catch (error) {
        console.error("Error fetching today's reminders:", error);
        setTodayRemindersCount(0);
      }
    };

    fetchTodayReminders();
  }, [user?.user_id, company?.id]);

  // Fetch loans due today from amortization schedule for company 22
  useEffect(() => {
    const fetchLoansDueToday = async () => {
      if (company?.id !== 22) return;

      try {
        const token = localStorage.getItem("token");
        const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

        const response = await axios.get(
          "https://zedlenders.pickmesms.com/items/amortization",
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: {
                due_date: { _eq: today },
                status: { _eq: "pending" },
              },
              aggregate: { count: "*" },
            },
          },
        );

        setLoansDueToday(response.data.data?.[0]?.count || 0);
      } catch (error) {
        console.error("Error fetching loans due today:", error);
        setLoansDueToday(0);
      }
    };

    fetchLoansDueToday();

    // Refresh every 60 seconds
    const interval = setInterval(fetchLoansDueToday, 60000);
    return () => clearInterval(interval);
  }, [company?.id]);

  // Count borrowers who signed up via the KwachaPlus mobile app in the last
  // 30 days. The placeholder `2020-01-01` we backfilled on legacy borrowers
  // is older than 30 days so it's naturally excluded by the `_gte` filter.
  useEffect(() => {
    if (!company?.id) return;
    setDailyDisbursementLimit(Number(company?.daily_disbursement_limit || 0));
    setDailyDisbursementWarnPct(
      Number(company?.daily_disbursement_warn_pct || 80),
    );
    const fetchLimitSettings = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/companies/${company.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fields: "daily_disbursement_limit,daily_disbursement_warn_pct",
            },
          },
        );
        const limit = Number(response.data.data?.daily_disbursement_limit || 0);
        const warnPct = Number(
          response.data.data?.daily_disbursement_warn_pct || 80,
        );
        setDailyDisbursementLimit(limit);
        setDailyDisbursementWarnPct(warnPct);
        if (
          company &&
          (company.daily_disbursement_limit !== limit ||
            company.daily_disbursement_warn_pct !== warnPct)
        ) {
          setCompany({
            ...company,
            daily_disbursement_limit: limit,
            daily_disbursement_warn_pct: warnPct,
          });
        }
      } catch (error) {
        console.error("Failed to load daily disbursement limit:", error);
      }
    };

    fetchLimitSettings();
  }, [company?.id]);

  useEffect(() => {
    const fetchTodayDisbursements = async () => {
      const token = localStorage.getItem("token");
      if (!company?.id || !token) return;
      try {
        const startOfToday = new Date();
        const endOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        endOfToday.setHours(23, 59, 59, 999);

        const response = await axios.get(
          "https://zedlenders.pickmesms.com/items/transactions",
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fields: "amount,transaction_type,transaction_date",
              filter: {
                company: company.id,
                transaction_type: "Loan Disbursement",
                transaction_date: {
                  _between: [
                    startOfToday.toISOString(),
                    endOfToday.toISOString(),
                  ],
                },
              },
              limit: -1,
            },
          },
        );

        const total = (response.data.data || []).reduce(
          (sum, t) => sum + Math.abs(Number(t.amount || 0)),
          0,
        );
        setTodayDisbursedTotal(total);
      } catch (error) {
        console.error("Error fetching today's disbursements:", error);
        setTodayDisbursedTotal(0);
      }
    };

    fetchTodayDisbursements();
    const interval = setInterval(fetchTodayDisbursements, 10000);
    const handleFocus = () => fetchTodayDisbursements();
    const handleVisibility = () => {
      if (!document.hidden) fetchTodayDisbursements();
    };
    const handleDisbursementEvent = () => fetchTodayDisbursements();
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("disbursement:created", handleDisbursementEvent);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(
        "disbursement:created",
        handleDisbursementEvent,
      );
    };
  }, [company?.id]);

  const openLimitModal = () => {
    setLimitDraft(dailyDisbursementLimit || 0);
    setWarnPctDraft(dailyDisbursementWarnPct || 80);
    setLimitModalOpen(true);
  };

  const saveLimitSettings = async () => {
    const safeLimit = Number(limitDraft || 0);
    const safeWarnPct = Math.min(Math.max(Number(warnPctDraft || 80), 1), 100);

    try {
      const token = localStorage.getItem("token");
      if (company?.id && token) {
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/companies/${company.id}`,
          {
            daily_disbursement_limit: safeLimit,
            daily_disbursement_warn_pct: safeWarnPct,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      setDailyDisbursementLimit(safeLimit);
      setDailyDisbursementWarnPct(safeWarnPct);
      if (company) {
        setCompany({
          ...company,
          daily_disbursement_limit: safeLimit,
          daily_disbursement_warn_pct: safeWarnPct,
        });
      }
      setLimitModalOpen(false);
    } catch (error) {
      console.error("Failed to save daily disbursement limit:", error);
    }
  };

  const limitWarnThreshold =
    dailyDisbursementLimit > 0
      ? dailyDisbursementLimit * (dailyDisbursementWarnPct / 100)
      : 0;
  const limitReached =
    dailyDisbursementLimit > 0 && todayDisbursedTotal >= dailyDisbursementLimit;
  const limitNear =
    dailyDisbursementLimit > 0 &&
    todayDisbursedTotal >= limitWarnThreshold &&
    !limitReached;

  const limitTagColor = limitReached ? "red" : limitNear ? "gold" : "green";
  const limitTagText = limitReached
    ? "Limit reached"
    : limitNear
      ? "Near limit"
      : "Within limit";

  return (
    <header className="main-header">
      <a href="" className="logo">
        <span className="logo-mini">
          <b>A</b>LT
        </span>

        <span
          className="logo-lg"
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          {company?.logo && (
            <img
              src={company.logo}
              alt="Company Logo"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          )}
          <b>{company?.company_name || ""}</b>
        </span>
      </a>

      <nav className="navbar navbar-static-top">
        {/* Sidebar toggle button*/}
        <a
          href="#"
          className="sidebar-toggle"
          data-toggle="push-menu"
          role="button"
        >
          <span className="sr-only">Toggle navigation</span>
          <span className="icon-bar" />
          <span className="icon-bar" />
          <span className="icon-bar" />
        </a>
        {/* Layout toggle: side menu <-> top menu. Sits next to the hamburger.
            NOTE: deliberately NOT using the `sidebar-toggle` class — AdminLTE
            injects a hamburger glyph via its :before, which would collide with
            our icon. We replicate its float/padding/height manually instead. */}
        <Tooltip
          title={
            navLayout === "top" ? "Switch to side menu" : "Switch to top menu"
          }
        >
          <a
            href="#"
            role="button"
            onClick={(e) => {
              e.preventDefault();
              toggleNavLayout();
            }}
            style={{
              float: "left",
              padding: "18px 15px 12px",
              fontSize: "18px",
              lineHeight: "20px",
              color: "inherit",
            }}
          >
            <span className="sr-only">Toggle menu layout</span>
            <LayoutOutlined />
          </a>
        </Tooltip>
        <div className="navbar-custom-menu">
          <ul className="nav navbar-nav">
            {company.plan > 0 && planStatus === "active" && (
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: isMobile ? "5px" : "20px",
                  marginTop: "5px",
                }}
                className={isMobile ? "mobile-hidden" : ""}
              >
                <Space
                  style={{
                    padding: isMobile ? "4px 8px" : "8px 16px",
                    background: "#fffbe6",
                    borderRadius: "8px",
                    border: "1px solid #ffe58f",
                    fontSize: isMobile ? "12px" : "14px",
                  }}
                >
                  <ClockCircleOutlined style={{ color: "#faad14" }} />
                  <Text
                    strong
                    style={{
                      color: "#d48806",
                      fontSize: isMobile ? "11px" : "14px",
                    }}
                  >
                    {isMobile
                      ? `${daysLeft}d Left`
                      : `${daysLeft} Days Left on Current Plan`}
                  </Text>
                  <Button
                    type="primary"
                    size={isMobile ? "small" : "small"}
                    onClick={() => navigate("/contact-to-upgrade")}
                    style={{
                      backgroundColor: "#52c41a",
                      marginLeft: isMobile ? "4px" : "8px",
                      fontSize: isMobile ? "10px" : "12px",
                    }}
                  >
                    Renew
                  </Button>
                </Space>
              </li>
            )}

            {/* Free Trial Section - Only shows during trial period (7 days or less) */}
            {company.plan === 0 && trialStatus === "active" && (
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: isMobile ? "5px" : "20px",
                  marginTop: "5px",
                }}
                className={isMobile ? "mobile-hidden" : ""}
              >
                <Space
                  style={{
                    padding: isMobile ? "4px 8px" : "8px 16px",
                    background: "#fffbe6",
                    borderRadius: "8px",
                    border: "1px solid #ffe58f",
                  }}
                >
                  <ClockCircleOutlined style={{ color: "#faad14" }} />
                  <Text
                    strong
                    style={{
                      color: "#d48806",
                      fontSize: isMobile ? "11px" : "14px",
                    }}
                  >
                    {isMobile
                      ? `${daysLeft}d Trial`
                      : `${daysLeft} Days Left on Free Trial`}
                  </Text>
                  <Button
                    type="primary"
                    size={isMobile ? "small" : "small"}
                    onClick={() => navigate("/contact-to-upgrade")}
                    style={{
                      backgroundColor: "#52c41a",
                      marginLeft: isMobile ? "4px" : "8px",
                      fontSize: isMobile ? "10px" : "12px",
                    }}
                  >
                    {isMobile ? "Upgrade" : "Upgrade Now"}
                  </Button>
                </Space>
              </li>
            )}

            <li
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: isMobile ? "5px" : "20px",
                marginTop: "10px",
                cursor: "pointer",
              }}
              onClick={() => navigate("/loans/2")}
            >
              {/* <Space>
                <Badge size="small" count={notificationCount} showZero>
                  <BellOutlined
                    style={{
                      fontSize: isMobile ? "14px" : "16px",
                      color: "#00000073",
                      padding: isMobile ? "6px" : "8px",
                      borderRadius: "80%",
                      backgroundColor: "#f0f2f5",
                    }}
                  />
                </Badge>
              </Space> */}
            </li>
            {/* <li
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "20px",
                marginTop: "10px",
              }}
            >
              <Tooltip title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                <Switch
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  checkedChildren={<BulbFilled />}
                  unCheckedChildren={<BulbOutlined />}
                />
              </Tooltip>
            </li> */}
            <li
              style={{
                display: "flex",
                alignItems: "center",
                flex: isMobile ? "0 0 auto" : "1 1 auto",
                minWidth: 0,
                marginRight: isMobile ? "5px" : "10px",
                marginTop: "10px",
              }}
            >
              {user?.role === "Administrator" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: isMobile ? "6px" : "10px",
                    marginRight: isMobile ? "6px" : "12px",
                    maxWidth: isMobile ? "100%" : "60vw",
                    minWidth: 0,
                  }}
                >
                  <Text
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: skin === "skin-black" ? "#666" : "#ffffff",
                      whiteSpace: "normal",
                    }}
                  >
                    <span
                      style={{
                        color: "#078930",
                        fontWeight: 700,
                        marginRight: 6,
                      }}
                    >
                      K {formatAmount(todayDisbursedTotal, isMobile)}
                    </span>
                    Disbursed Today
                  </Text>
                  <Tag
                    color={
                      dailyDisbursementLimit > 0 ? limitTagColor : "default"
                    }
                    style={{ marginInlineEnd: 0 }}
                  >
                    {dailyDisbursementLimit > 0 ? limitTagText : "No limit"}
                  </Tag>
                  <Tooltip title="Set daily disbursement limit">
                    <Button
                      type="text"
                      size="small"
                      icon={
                        limitReached || limitNear ? (
                          <WarningOutlined />
                        ) : (
                          <SettingOutlined />
                        )
                      }
                      onClick={openLimitModal}
                      style={{
                        color: skin === "skin-black" ? "#666" : "#ffffff",
                        padding: 0,
                        height: "auto",
                      }}
                    />
                  </Tooltip>
                </div>
              )}

              {/* Display accounts horizontally - Administrator only */}
              {user?.role === "Administrator" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    flexWrap: "nowrap",
                    gap: isMobile ? "4px" : "8px",
                    maxWidth: isMobile ? "100%" : "45vw",
                    minWidth: 0,
                    overflowX: isMobile ? "visible" : "auto",
                    whiteSpace: "nowrap",
                    paddingBottom: isMobile ? 0 : 4,
                  }}
                >
                  {accounts && accounts.length > 0 ? (
                    <>
                      {/* Total Balance */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        <Space
                          style={{
                            padding: isMobile ? "2px 6px" : "4px 12px 0px 6px",
                            background: "#FFB81C", // Orange for total
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                          onClick={() => navigate("/accountsManagement")}
                        >
                          <DollarOutlined
                            style={{
                              color: "#ffffff",
                            }}
                          />
                          <Text
                            strong
                            style={{
                              color: "#ffffff",
                              margin: 0,
                              fontSize: isMobile ? "10px" : "14px",
                            }}
                          >
                            K
                            {(() => {
                              const totalBalance = accounts
                                .filter(
                                  (account) =>
                                    account.status === "active" ||
                                    !account.status,
                                )
                                .reduce(
                                  (sum, account) =>
                                    sum + (parseFloat(account?.balance) || 0),
                                  0,
                                );
                              return isMobile
                                ? totalBalance > 1000
                                  ? `${(totalBalance / 1000).toFixed(1)}k`
                                  : totalBalance.toFixed(0)
                                : totalBalance
                                    .toFixed(2)
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                            })()}
                          </Text>
                        </Space>
                        <Text
                          style={{
                            fontSize: isMobile ? "8px" : "10px",
                            color: skin === "skin-black" ? "#666" : "#ffffff",
                          }}
                        >
                          Total Balance
                        </Text>
                      </div>

                      {/* Individual Accounts */}
                      {accounts
                        .filter(
                          (account) =>
                            account.status === "active" || !account.status,
                        )
                        .map((account) => (
                          <div
                            key={account.id}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flex: "0 0 auto",
                            }}
                          >
                            <Space
                              style={{
                                padding: isMobile
                                  ? "2px 6px"
                                  : "4px 12px 0px 6px",
                                background: account.is_default
                                  ? "#078930"
                                  : "#f8f9fa", // Green for default
                                borderRadius: 8,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate("/floatManagement")}
                            >
                              <BankOutlined
                                style={{
                                  color: account.is_default
                                    ? "#ffffff"
                                    : "#078930",
                                }}
                              />
                              <Text
                                strong
                                style={{
                                  color: account.is_default
                                    ? "#ffffff"
                                    : "#2d3748",
                                  margin: 0,
                                  fontSize: isMobile ? "10px" : "14px",
                                }}
                              >
                                K
                                {isMobile
                                  ? (account?.balance || 0) > 1000
                                    ? `${(
                                        (account?.balance || 0) / 1000
                                      ).toFixed(1)}k`
                                    : (account?.balance || 0).toFixed(0)
                                  : (account?.balance || 0)
                                      .toFixed(2)
                                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                              </Text>
                            </Space>
                            <Text
                              style={{
                                fontSize: isMobile ? "8px" : "10px",
                                color:
                                  skin === "skin-black" ? "#666" : "#ffffff",
                              }}
                            >
                              {isMobile
                                ? account.name.substring(0, 6) +
                                  (account.name.length > 6 ? "..." : "")
                                : account.name}
                            </Text>
                          </div>
                        ))}
                    </>
                  ) : (
                    // Legacy fallback to company balance
                    <Space
                      style={{
                        padding: isMobile ? "2px 8px" : "4px 16px",
                        background: "#f8f9fa",
                        borderRadius: 8,
                      }}
                      onClick={() => navigate("/accountsManagement")}
                    >
                      <DollarOutlined style={{ color: "#52c41a" }} />
                      <Text
                        strong
                        style={{
                          color: skin === "skin-black" ? "#2d3748" : "#ffffff",
                          fontSize: isMobile ? "10px" : "14px",
                        }}
                      >
                        K
                        {isMobile
                          ? (company?.balance || 0) > 1000
                            ? `${((company?.balance || 0) / 1000).toFixed(1)}k`
                            : (company?.balance || 0).toFixed(0)
                          : (company?.balance || 0)
                              ?.toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </Text>
                    </Space>
                  )}
                </div>
              )}

              {/* Expense Approval Notification Bell - Only for Administrators (except company 22) */}
              {user?.role === "Administrator" && company?.id !== 22 && (
                <Badge
                  size="small"
                  count={pendingExpensesCount}
                  showZero={false}
                  style={{ marginRight: isMobile ? "8px" : "12px" }}
                >
                  <Tooltip
                    title={`${pendingExpensesCount} expense${pendingExpensesCount !== 1 ? "s" : ""} pending approval`}
                  >
                    <Button
                      type="text"
                      icon={<BellOutlined />}
                      onClick={() => navigate("/expense-approvals")}
                      style={{
                        color:
                          pendingExpensesCount > 0 ? "#009900" : "#00000073",
                        fontSize: isMobile ? "14px" : "16px",
                        padding: isMobile ? "4px" : "8px",
                      }}
                    />
                  </Tooltip>
                </Badge>
              )}

              {/* Loans Due Today Bell - Only for Company 22 */}
              {company?.id === 22 && (
                <Badge
                  size="small"
                  count={loansDueToday}
                  showZero={false}
                  style={{ marginRight: isMobile ? "8px" : "12px" }}
                >
                  <Tooltip
                    title={`${loansDueToday} loan${loansDueToday !== 1 ? "s" : ""} due today`}
                  >
                    <Button
                      type="text"
                      icon={<BellOutlined />}
                      onClick={() => navigate("/amortizations")}
                      style={{
                        color: loansDueToday > 0 ? "#ff4d4f" : "#00000073",
                        fontSize: isMobile ? "14px" : "16px",
                        padding: isMobile ? "4px" : "8px",
                      }}
                    />
                  </Tooltip>
                </Badge>
              )}

              {/* Calendar Icon with Badge */}
              <Badge count={todayRemindersCount} showZero={false} size="small">
                <Tooltip
                  title={`${todayRemindersCount} reminder${todayRemindersCount !== 1 ? "s" : ""} today`}
                >
                  <Button
                    type="text"
                    icon={<CalendarOutlined />}
                    onClick={() => navigate("/calendar")}
                    style={{
                      color: "#078930",
                      fontSize: isMobile ? "16px" : "18px",
                      padding: isMobile ? "4px" : "8px",
                    }}
                  />
                </Tooltip>
              </Badge>

              {/* Video Tutorials Icon */}
              {/* <Tooltip title="Help & Tutorials">
                <Button
                  type="text"
                  icon={<PlayCircleOutlined />}
                  onClick={() => navigate("/tutorials")}
                  style={{
                    color: "#1890ff",
                    fontSize: isMobile ? "16px" : "18px",
                    padding: isMobile ? "4px" : "8px",
                    marginLeft: isMobile ? "4px" : "8px",
                  }}
                />
              </Tooltip> */}
            </li>

            {/* <li className="dropdown notifications-menu">
              <a
                href="#"
                className="dropdown-toggle"
                data-toggle="dropdown"
                aria-expanded="true"
              >
                <i className="fa fa-bell-o" />
                <span className="label label-danger">3</span>
              </a>
              <ul className="dropdown-menu">
                <li className="header">0 PTP(s) Due Today</li>
                <li>
                  <ul className="menu">
                    <li>
                      <ul className="menu"></ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </li> */}

            <li class="dropdown">
              <a
                href="#"
                class="dropdown-toggle"
                data-toggle="dropdown"
                aria-expanded="true"
              >
                <i class="fa fa-link"></i> <span>Settings</span>{" "}
                <span class="caret"></span>
              </a>
              <ul class="dropdown-menu" role="menu">
                {/* Menu items rendered based on roles */}
                {(() => {
                  // Check if user is the specific branch manager with restricted access
                  const isSpecificBranchManager =
                    user?.user_id === BRANCH_MANAGER_ROLE_ID ||
                    localStorage.getItem("userId") === BRANCH_MANAGER_ROLE_ID ||
                    user?.id === BRANCH_MANAGER_ROLE_ID;

                  // Check if user is an administrator
                  const isAdmin = user?.role === "Administrator";

                  // Check if user is a branch manager (any branch manager)
                  const isBranchManager = user?.role === "Branch Manager";

                  // Define the restricted items for the specific branch manager
                  // Note: SMS and email are now admin-only and handled separately
                  const restrictedItems = [
                    "loanProducts",
                    "investmentProducts",
                    "branches",
                    "floatManagement",
                  ];

                  // Helper function to check if a menu item should be shown based on role and restrictions
                  const shouldShowMenuItem = (itemName) => {
                    // Admin can always see everything
                    if (isAdmin) return true;

                    // For the specific branch manager, hide restricted items
                    if (isBranchManager && isSpecificBranchManager) {
                      return !restrictedItems.includes(itemName);
                    }

                    // For other branch managers, follow admin-only restrictions
                    // This maintains the original permission logic for other branch managers
                    if (isBranchManager) {
                      return ![
                        "loanProducts",
                        "investmentProducts",
                        "branches",
                        "floatManagement",
                      ].includes(itemName);
                    }

                    // For other roles, don't show these items
                    return false;
                  };

                  return (
                    <>
                      {/* Users - Visible ONLY to Admin */}
                      {isAdmin && (
                        <li>
                          <Link to="/users">Users</Link>
                        </li>
                      )}

                      {/* Loan Products - Based on role restrictions */}
                      {shouldShowMenuItem("loanProducts") && (
                        <li>
                          <Link to="/loanProducts">Loan Products</Link>
                        </li>
                      )}

                      {/* Investment Products - Based on role restrictions */}
                      {shouldShowMenuItem("investmentProducts") && (
                        <li>
                          <Link to="/investmentProducts">
                            Investment Products
                          </Link>
                        </li>
                      )}

                      {/* Branches - Based on role restrictions */}
                      {shouldShowMenuItem("branches") && (
                        <li>
                          <Link to="/branches">Branches</Link>
                        </li>
                      )}

                      {/* Float Management - Based on role restrictions */}
                      {shouldShowMenuItem("floatManagement") && (
                        <li>
                          <Link to="/floatManagement">Float Management</Link>
                        </li>
                      )}

                      {/* Accounts Management - Based on role restrictions */}
                      {shouldShowMenuItem("floatManagement") && (
                        <li>
                          <Link to="/accountsManagement">
                            Accounts Management
                          </Link>
                        </li>
                      )}

                      {/* Commodities - Visible to Admin and Branch Manager for company 12 only */}
                      {(isAdmin || isBranchManager) && company?.id === 12 && (
                        <li>
                          <Link to="/commodities">Manage Commodities</Link>
                        </li>
                      )}

                      {/* SMS Setup - Visible ONLY to Admin */}
                      {/* {isAdmin && (
                        <li>
                          <Link to="/email">SMS Setup</Link>
                        </li>
                      )} */}

                      {/* Email Setup - Visible ONLY to Admin */}
                      {/* {isAdmin && (
                        <li>
                          <Link to="/email">Email Setup</Link>
                        </li>
                      )} */}

                      {/* Profile - Visible to everyone */}
                      <li>
                        <Link to="/profile">Profile</Link>
                      </li>

                      {/* Logout - Visible to everyone */}
                      <li>
                        <a href="#" onClick={handleLogout}>
                          Logout
                        </a>
                      </li>
                    </>
                  );
                })()}
              </ul>
            </li>
          </ul>
        </div>
      </nav>
      <Modal
        title="Daily Disbursement Limit"
        open={limitModalOpen}
        onOk={saveLimitSettings}
        onCancel={() => setLimitModalOpen(false)}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <Text strong>Daily limit (ZMW)</Text>
            <InputNumber
              value={limitDraft}
              min={0}
              style={{ width: "100%", marginTop: 8 }}
              formatter={(value) => `K ${Number(value || 0).toLocaleString()}`}
              parser={(value) => (value || "").replace(/K\s?|,/g, "")}
              onChange={(value) => setLimitDraft(value || 0)}
            />
            <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
              Set to 0 to disable the limit warning.
            </Text>
          </div>
          <div>
            <Text strong>Warn me at (%)</Text>
            <InputNumber
              value={warnPctDraft}
              min={1}
              max={100}
              style={{ width: "100%", marginTop: 8 }}
              formatter={(value) => `${value}%`}
              parser={(value) => (value || "").replace("%", "")}
              onChange={(value) => setWarnPctDraft(value || 80)}
            />
          </div>
        </div>
      </Modal>
    </header>
  );
};

export default Navbar;
